import { useParams } from "react-router-dom";
import { logo } from "../../asset";
import { schoolName } from "../../util/Constants";
import { useEffect, useState } from "react";
import FormFieldRenderer from "../../components/FromFieldRenderer";
import { toast } from "sonner";
import examsApis from "../../api/ExamsApis";
import { ExamDetails } from "../../types/ExamType";
import Tag from "../../components/Tags";
import StepsBar, { StepperActions } from "../../components/StepsBar";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { useSelector } from "react-redux";
import { getSchoolBranchByCode } from "../../features/school-branch-controller/schoolBranchSlice";

const mapQuestionToField = (q: any) => ({
  id: q.questionId,
  fieldName: q.question,
  fieldAbbr: `q_${q.questionId}`,
  fieldType: q.questionType,
  fieldDescription: q.question,
  isFieldRequired: q.isMandatory,
  options: q.options || [],
  marks: q.marks > 0 ? q.marks : undefined,
});

export default function FormLink() {
  const { examSeqId } = useParams<{ examSeqId: string }>();
  const [form, setForm] = useState<ExamDetails | null>(null);
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState<any>({});
  const { branchCode } = useParams();

  const dispatch = useDispatch<AppDispatch>();
  const { branchDetails } = useSelector(
    (state: RootState) => state.schoolBranch,
  );

  useEffect(() => {
    dispatch(getSchoolBranchByCode());
  }, [dispatch, branchCode]);

  useEffect(() => {
    if (!examSeqId) return;

    (async () => {
      const res = await examsApis.getExamsBySeqId(examSeqId);

      if (res?.status === 1 && res.data) {
        setForm(res.data);
      }
    })();
  }, [examSeqId]);

  const sections = form?.examSections || [];
  const currentSection = sections[step];

  const isSectionValid = () => {
    if (!currentSection) return false;

    return currentSection.examQuestions.every((q: any) => {
      if (!q.isMandatory) return true;

      const key = `q_${q.questionId}`;
      const value = formData[key];

      if (value === undefined || value === null) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      if (Array.isArray(value) && value.length === 0) return false;

      return true;
    });
  };

  const handleChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev: any) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const totalSteps = sections.length;
  const isFirst = step === 0;
  const isLast = step === totalSteps - 1;

  const onNext = () => {
    if (!isLast) {
      setStep((prev) => prev + 1);
    }
  };

  const onBack = () => {
    if (!isFirst) {
      setStep((prev) => prev - 1);
    }
  };

  return (
    <main className="min-h-screen bg-background-100 text-neutral-400 p-5">
      <section className="max-w-4xl mx-auto flex flex-col gap-5">
        <header className="flex flex-col items-center gap-2">
          <img src={branchDetails?.schoolBranchIcon} alt="logo" className="h-24" />
          <h3 className="text-2xl font-semibold">{branchDetails?.schoolBranchName}</h3>
          <p className="text-sm text-neutral-200">
            Answer the following questions to the best of your knowledge.
          </p>
        </header>
        <section className="flex flex-col gap-2">
          <div className="bg-background-100 rounded-xl shadow-md p-6 flex mx-2 flex-col gap-5">
            <h3 className="text-lg font-semibold text-neutral-700">
              {form?.examName}
            </h3>
            <div className="px-10 flex justify-center items-center">
              {/* Steps */}
              {sections.length > 0 && (
                <StepsBar
                  current={step + 1}
                  sections={sections.map((s: any) => ({
                    id: s.examSectionId,
                    name: s.sectionName,
                  }))}
                  onStepClick={(clickedStep) => setStep(clickedStep - 1)}
                />
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4 p-2">
            {sections.length === 0 && (
              <p className="text-center text-neutral-300">
                This form has no sections yet
              </p>
            )}
            <div className="grid gap-4">
              {currentSection?.examQuestions?.length === 0 ? (
                <div className="bg-background-100 rounded-lg shadow-md p-6 text-center">
                  <p className="text-neutral-300 text-sm">
                    No questions available for this section right now.
                  </p>
                </div>
              ) : (
                currentSection?.examQuestions?.map((q: any) => (
                  <div
                    key={q.questionId}
                    className="flex bg-background-100 p-4 shadow-md rounded-lg flex-col gap-3"
                  >
                    {q.marks > 0 && <Tag text={`${q.marks} : Marks`} />}

                    <FormFieldRenderer
                      field={mapQuestionToField(q)}
                      formData={formData}
                      onChange={handleChange}
                      setFormData={setFormData}
                    />
                  </div>
                ))
              )}
            </div>

            <StepperActions
              isFirst={isFirst}
              isLast={isLast}
              isNextDisabled={!isSectionValid()}
              onBack={onBack}
              onNext={onNext}
              onSubmit={() => toast.success("Saved")}
            />
          </div>
        </section>
      </section>
    </main>
  );
}
